// File: src/components/press/PressPost.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { useProfile } from '../../context/ProfileContext';
import useSongActions from '../../hooks/useSongActions';
import { BADGE_TIERS } from '../../config/constants';
import dropdownIcon from '../../assets/dropdown_icon.png';

// 🌟 Tier Style Helper for Visual Accent Shading (Green = Fan, Blue = SupaFan, Pink = UltraFan)
const getTierStyles = (audience, isDarkMode) => {
    // 1. Public Posts
    if (!audience || audience.everyone) {
        return {
            badgeColor: 'text-[#ff00cc] bg-[#ff00cc]/10 border-[#ff00cc]/20',
            containerStyle: isDarkMode 
                ? 'bg-zinc-900 border-zinc-800' 
                : 'bg-white border-slate-100',
            buttonBg: 'bg-[#ff00cc] hover:bg-[#e000b4]',
            accentBorder: isDarkMode ? 'border-zinc-800' : 'border-slate-100',
            tierName: 'Public'
        };
    }

    // 2. Subscriber Tier
    if (audience.subscribers) {
        return {
            badgeColor: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
            containerStyle: isDarkMode 
                ? 'bg-purple-950/20 border-purple-500/40' 
                : 'bg-purple-50/50 border-purple-200/80',
            buttonBg: 'bg-purple-600 hover:bg-purple-700',
            accentBorder: 'border-purple-500/30',
            tierName: 'Subscriber'
        };
    }

    // 🌟 3. Lowest Point Tier First: FAN (Green)
    if (audience.fans) {
        return {
            badgeColor: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30',
            containerStyle: isDarkMode 
                ? 'bg-emerald-950/20 border-emerald-500/40 shadow-emerald-950/20' 
                : 'bg-emerald-50/50 border-emerald-200/80 shadow-emerald-100/50',
            buttonBg: 'bg-emerald-500 hover:bg-emerald-600',
            accentBorder: 'border-emerald-500/30',
            tierName: 'Fan'
        };
    }

    // 🌟 4. SUPAFAN (Blue)
    if (audience.supafans) {
        return {
            badgeColor: 'text-sky-500 bg-sky-500/10 border-sky-500/30',
            containerStyle: isDarkMode 
                ? 'bg-sky-950/20 border-sky-500/40 shadow-sky-950/20' 
                : 'bg-sky-50/50 border-sky-200/80 shadow-sky-100/50',
            buttonBg: 'bg-sky-500 hover:bg-sky-600',
            accentBorder: 'border-sky-500/30',
            tierName: 'SupaFan'
        };
    }

    // 🌟 5. ULTRAFAN (Pink)
    if (audience.ultrafans) {
        return {
            badgeColor: 'text-pink-500 bg-pink-500/10 border-pink-500/30',
            containerStyle: isDarkMode 
                ? 'bg-pink-950/20 border-pink-500/40 shadow-pink-950/20' 
                : 'bg-pink-50/50 border-pink-200/80 shadow-pink-100/50',
            buttonBg: 'bg-[#ff00cc] hover:bg-pink-600',
            accentBorder: 'border-pink-500/30',
            tierName: 'UltraFan'
        };
    }

    return {
        badgeColor: 'text-slate-400 bg-slate-500/10 border-slate-500/20',
        containerStyle: isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-100',
        buttonBg: 'bg-zinc-800 hover:bg-zinc-700',
        accentBorder: isDarkMode ? 'border-zinc-800' : 'border-slate-100',
        tierName: 'Exclusive'
    };
};

const getContextHeader = (sourceContext, artistProfile, engagementInfo, isBoosted) => {
    if (sourceContext === 'forYou') {
        if (engagementInfo?.isSubscriber) return "From an Artist You Subscribe To";
        if (engagementInfo?.hasTipped) return "From an Artist You Tipped";
        if (engagementInfo?.hasPlaylistedSong) return "From an Artist You Playlisted";
        if (engagementInfo?.hasSavedSong) return "From an Artist You Saved";
        if (engagementInfo?.hasHotRating) return "From an Artist You Rated Hot";
        return "From an Artist You Follow"; 
    }
    
    if (sourceContext === 'home' && artistProfile?.city && artistProfile?.state) {
        return `From ${artistProfile.city}, ${artistProfile.state}`;
    }

    if (isBoosted) return "Sponsored Post";
    
    return "Atomic News Desk";
};

const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

const PressPostContent = ({ 
    post, 
    artistProfile,
    engagementInfo,
    sourceContext = 'home',
    observe,
    isOwner = false,
    userFanScore,
    onEdit,
    onDelete,
}) => {
    const postRef = useRef(null);
    const navigate = useNavigate();
    const { user, isDarkMode } = useProfile();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [lazyFanScore, setLazyFanScore] = useState(null);
    const [isScoreLoading, setIsScoreLoading] = useState(false);

    const { isSubscribed } = useSongActions({
        songId: post.songId || null,
        artistId: post.artistId 
    });

    const audience = useMemo(() => post?.targetAudience, [post]);
    const isPublic = useMemo(() => !audience || audience.everyone === true, [audience]);

    const requiresTierPoints = useMemo(() => {
        if (isPublic) return false;
        if (audience?.subscribers && isSubscribed) return false;
        return Boolean(audience?.fans || audience?.supafans || audience?.ultrafans);
    }, [isPublic, audience, isSubscribed]);

    // Lazy point lookup for gated posts
    useEffect(() => {
        if (!requiresTierPoints || !user?.uid || !post.artistId || lazyFanScore !== null) {
            return;
        }

        let isMounted = true;
        const fetchArtistFanScore = async () => {
            setIsScoreLoading(true);
            try {
                const db = getFirestore();
                const tipRef = doc(db, `users/${user.uid}/artistsTipped/${post.artistId}`);
                const tipSnap = await getDoc(tipRef);

                if (isMounted) {
                    if (tipSnap.exists()) {
                        const data = tipSnap.data();
                        const points = data.totalPointsTipped ?? data.tipAmountAUD ?? data.cumulativeAmount ?? 0;
                        setLazyFanScore(Number(points));
                    } else {
                        setLazyFanScore(0);
                    }
                }
            } catch (err) {
                console.error("Error fetching lazy FanScore:", err);
                if (isMounted) setLazyFanScore(0);
            } finally {
                if (isMounted) setIsScoreLoading(false);
            }
        };

        fetchArtistFanScore();

        return () => { isMounted = false; };
    }, [requiresTierPoints, user?.uid, post.artistId, lazyFanScore]);

    // Access evaluation
    const hasAccess = useMemo(() => {
        if (isPublic) return true;
        if (audience?.subscribers && isSubscribed) return true;

        const score = Number(userFanScore || lazyFanScore || 0);

        if (audience?.fans && score >= (BADGE_TIERS.FAN?.minPoints || 100)) return true;
        if (audience?.supafans && score >= (BADGE_TIERS.SUPAFAN?.minPoints || 500)) return true;
        if (audience?.ultrafans && score >= (BADGE_TIERS.ULTRAFAN?.minPoints || 2000)) return true;

        return false;
    }, [isPublic, audience, isSubscribed, userFanScore, lazyFanScore]);

    // 🌟 SINGLE SOURCE OF TRUTH: Unread state handled strictly by viewport observation
    useEffect(() => {
        const element = postRef.current;
        if (element && observe && post.isRead === false) {
            observe(element);
        }
    }, [observe, post.id, post.isRead]);

    const logPostInteraction = useMemo(() => {
        const functions = getFunctions();
        return httpsCallable(functions, 'logPostInteraction');
    }, []);

    const [wasInitiallyUnread, setWasInitiallyUnread] = useState(post.isRead === false);
    
    const contextHeader = useMemo(() => 
        getContextHeader(sourceContext, artistProfile, engagementInfo, post.isBoosted), 
        [sourceContext, artistProfile, engagementInfo, post.isBoosted]
    );

    const isVideo = post.mediaUrl && (post.mediaUrl.includes('.mp4') || post.mediaUrl.includes('.mov'));

    const { newsHeadline, newsText } = useMemo(() => {
        if (post.atomifiedTitle && post.atomifiedText) {
            return { newsHeadline: post.atomifiedTitle, newsText: post.atomifiedText };
        }
        if (post.title) {
            return { newsHeadline: post.title, newsText: post.text || post.body || '' };
        }
        const oldText = post.officialText || post.atomifiedText || '';
        if (oldText) {
            const parts = oldText.split('\n');
            return { newsHeadline: parts[0] || 'Atomic Music Update', newsText: parts.slice(1).join('\n').trim() };
        }
        return { newsHeadline: "Atomic Music Update", newsText: "" };
    }, [post]);

    const excerpt = useMemo(() => truncateText(newsText, 160), [newsText]);

    const isSponsorshipActive = useMemo(() => {
        if (post.isBoosted === true) return true;
        if (!post.boostedAt) return false;
        const boostedAtDate = post.boostedAt.toDate ? post.boostedAt.toDate() : new Date(post.boostedAt);
        const expirationDate = new Date(boostedAtDate);
        expirationDate.setDate(expirationDate.getDate() + 30);
        return new Date() < expirationDate;
    }, [post.boostedAt, post.isBoosted]);

    // Get color themes for Fan / SupaFan / UltraFan shading
    const tierStyles = useMemo(() => getTierStyles(audience, isDarkMode), [audience, isDarkMode]);

    const requiredTierLabel = useMemo(() => {
        if (!audience) return "Supporters";
        const labels = [];
        if (audience.subscribers) labels.push("Subscribers");
        if (audience.fans) labels.push("Fans");
        if (audience.supafans) labels.push("SupaFans");
        if (audience.ultrafans) labels.push("UltraFans");
        return labels.length > 0 ? labels.join(" / ") : "Supporters";
    }, [audience]);

    const handleNavigate = () => {
        if (!hasAccess) {
            toast.error(`This post is exclusively for ${requiredTierLabel}. Support this creator to unlock!`);
            return;
        }
        try {
            logPostInteraction({ postId: post.id, interactionType: 'click_through' });
        } catch (err) {
            console.error("Failed to log interaction:", err);
        }
        navigate(`/feed/${post.id}?from=${sourceContext || 'home'}`);
    };

    useEffect(() => {
        setWasInitiallyUnread(post.isRead === false);
    }, [post.id, post.isRead]);

    // 🌟 RENDER LOCKED TEASER (Full headline displayed, button overlaying sub-text area)
    if (!hasAccess) {
        return (
            <div
                ref={postRef}
                data-post-id={post.id}
                data-is-read={post.isRead}
                onClick={handleNavigate}
                className={`relative w-full max-w-md mx-auto rounded-3xl p-6 border shadow-sm transition-all duration-300 cursor-pointer overflow-hidden ${tierStyles.containerStyle}`}
            >
                {/* Header Badge */}
                <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${tierStyles.badgeColor}`}>
                        🔒 {tierStyles.tierName} Exclusive
                    </span>
                    {wasInitiallyUnread && (
                        <span className="h-2 w-2 rounded-full bg-[#ff00cc] animate-pulse" title="Unread"></span>
                    )}
                </div>

                {/* Full Headline Uncovered */}
                <h2 className={`text-lg font-black tracking-tight uppercase italic leading-tight mb-3 ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                    {newsHeadline}
                </h2>

                {/* Sub-Text Area covered by Unlock Prompt Bar */}
                <div className="mt-2 pt-3 border-t border-slate-200/40 dark:border-zinc-800/60 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-400 font-bold line-clamp-1">
                        {isScoreLoading ? "Verifying Access..." : `Locked for ${requiredTierLabel}`}
                    </p>

                    <button 
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/profile/${post.artistId}`);
                        }}
                        className={`px-4 py-2 text-white text-[10px] font-black uppercase tracking-wider rounded-xl shadow-md transition-all flex-shrink-0 active:scale-95 ${tierStyles.buttonBg}`}
                    >
                        Unlock Post
                    </button>
                </div>
            </div>
        );
    }

    // 🌟 RENDER UNLOCKED CARD (With Tier-Based Tint Shading)
    return (
        <div
            ref={postRef}
            data-post-id={post.id}
            data-is-read={post.isRead}
            onClick={handleNavigate}
            className={`relative w-full max-w-md mx-auto rounded-3xl shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border ${tierStyles.containerStyle}`}
        >
            {wasInitiallyUnread && (
                <span className="absolute top-4 left-4 block h-2.5 w-2.5 rounded-full bg-[#ff00cc] z-10 animate-pulse" title="Unread"></span>
            )}

            {isOwner && (
                <div className="absolute top-3 right-3 z-20">
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsMenuOpen(!isMenuOpen);
                        }}
                        className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-slate-100'}`}
                    >
                        <img src={dropdownIcon} alt="Menu" className={`w-5 h-5 ${isDarkMode ? 'invert opacity-60' : ''}`} />
                    </button>

                    {isMenuOpen && (
                        <div className={`absolute top-8 right-0 rounded-xl shadow-xl border w-36 overflow-hidden z-30 font-bold text-xs uppercase tracking-wider ${
                            isDarkMode ? 'bg-zinc-950 border-zinc-800 text-zinc-300' : 'bg-white border-slate-100 text-slate-700'
                        }`}>
                            <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); if (onEdit) onEdit(post); }}
                                className={`block w-full text-left px-4 py-3 transition-colors ${isDarkMode ? 'hover:bg-zinc-900' : 'hover:bg-slate-50'}`}
                            >
                                Edit Post
                            </button>
                            <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); if (onDelete) onDelete(post); }}
                                className={`block w-full text-left px-4 py-3 border-t transition-colors ${
                                    isDarkMode ? 'border-zinc-900 text-red-400 hover:bg-zinc-900' : 'border-slate-50 text-red-600 hover:bg-slate-50'
                                }`}
                            >
                                Delete Post
                            </button>
                        </div>
                    )}
                </div>
            )}

            {(contextHeader || post.isBoosted) && (
                <div className={`flex justify-between items-center px-5 pt-3.5 pb-2 border-b ${
                    isDarkMode ? 'border-zinc-800/60 bg-zinc-950/30' : 'border-slate-100/60 bg-slate-50/50'
                }`}>
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {contextHeader}
                    </p>
                    
                    {isSponsorshipActive && (
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-md ${
                            isDarkMode ? 'bg-[#ff00cc]/10 text-[#ff00cc]' : 'bg-pink-100 text-pink-700'
                        }`}>
                            Sponsored
                        </span>
                    )}
                </div>
            )}

            {post.mediaUrl && (
                <div className={`w-full h-56 overflow-hidden border-b ${isDarkMode ? 'bg-zinc-950 border-zinc-800/40' : 'bg-slate-100 border-slate-100'}`}>
                    {isVideo ? (
                        <video
                            key={`${post.id}-${post.mediaUrl}`} 
                            src={`${post.mediaUrl}#t=0.5`}
                            className="w-full h-full object-cover"
                            autoPlay 
                            muted 
                            loop 
                            playsInline
                            controls
                            onClick={(e) => e.stopPropagation()} 
                            preload="metadata"
                        >
                            Your browser does not support the video tag.
                        </video>
                    ) : (
                        <img 
                            src={post.mediaUrl} 
                            alt={newsHeadline} 
                            className="w-full h-full object-cover" 
                        />
                    )}
                </div>
            )}

            <div className="p-5 flex flex-col flex-grow">
                <h2 className={`text-lg font-black tracking-tight uppercase italic leading-tight mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {newsHeadline}
                </h2>

                {excerpt && (
                    <p className={`text-xs font-semibold leading-relaxed ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {excerpt}
                    </p>
                )}

                <div className={`mt-4 pt-2 border-t ${tierStyles.accentBorder} flex items-center justify-between`}>
                    <span className="text-xs font-black uppercase tracking-wider text-[#ff00cc] hover:underline flex items-center gap-1">
                        READ FULL ANNOUNCEMENT
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                    </span>
                </div>
            </div>
        </div>
    );
};

const PressPost = ({ post, observe, ...props }) => {
    if (!post) return null;
    return <PressPostContent post={post} observe={observe} {...props} />;
};

export default PressPost;